import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { ADMIN_API_BASE } from "../utils/apiConfig";

const TextEditor = ({ content = '', onContentChange }) => {
    const [editorHtml, setEditorHtml] = useState('');
    const quillRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (content) setEditorHtml(content);
    }, [content]);

    const getCsrfToken = async () => {
        try {
            const response = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            return data.csrfToken;
        } catch (error) {
            console.error('Error fetching CSRF token:', error);
            return null;
        }
    };

    const handleImageUpload = useCallback(async () => {
        const input = fileInputRef.current;
        if (!input) return;
        input.click();

        input.onchange = async () => {
            const files = input.files;
            if (!files || files.length === 0) return;

            for (const file of files) {
                const formData = new FormData();
                formData.append('image', file);

                try {
                    const token = await getCsrfToken();
                    if (!token) return;

                    const response = await axios.post(
                        `${ADMIN_API_BASE}/blog/upload-image`,
                        formData,
                        {
                            headers: {
                                'csrf-token': token,
                                'Content-Type': 'multipart/form-data',
                            },
                            withCredentials: true,
                        }
                    );

                    const imageUrl = response.data.url;
                    const quill = quillRef.current.getEditor();
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'image', imageUrl);
                    quill.setSelection(range.index + 1);
                } catch (error) {
                    console.error('Image upload failed:', error);
                }
            }

            input.value = '';
        };
    }, []);

    const handleLink = useCallback(() => {
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection();
        if (range && range.length > 0) {
            const url = prompt('Enter the link URL:');
            if (url) {
                quill.format('link', url);
            }
        } else {
            alert('Please select text to link.');
        }
    }, []);

    useEffect(() => {
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            const toolbar = quill.getModule('toolbar');
            toolbar.addHandler('image', handleImageUpload);
            toolbar.addHandler('link', handleLink);
        }
    }, [handleImageUpload, handleLink]);

    const toolbarOptions = [
        [{ header: [1, 2, 3, 4, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ align: [] }, { direction: 'rtl' }],
        ['link', 'image'],
        ['blockquote', 'code-block'],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        ['clean'],
    ];

    const handleChange = useCallback(
        (html) => {
            setEditorHtml(html);
            if (onContentChange) onContentChange(html);
        },
        [onContentChange]
    );

    return (
        <div
            className="mt-5 mb-5 quill-c"
            style={{
                overflowY: 'auto',
                border: '1px solid #ccc',
                borderRadius: '4px',
            }}
        >
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                multiple
            />
            <ReactQuill
                ref={quillRef}
                value={editorHtml}
                onChange={handleChange}
                modules={{ toolbar: toolbarOptions }}
                formats={[
                    'header',
                    'font',
                    'size',
                    'bold',
                    'italic',
                    'underline',
                    'strike',
                    'blockquote',
                    'list',
                    'bullet',
                    'indent',
                    'link',
                    'image',
                    'code-block',
                    'direction',
                    'align',
                    'color',
                    'background',
                ]}
                theme="snow"
            />
        </div>
    );
};

export default TextEditor;
